import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";

import { TableSortLabel } from "@mui/material";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { subscriptionStatusColors } from "../../../ui/colors";
import { IBillingSubscriptionItem, TBillingOrder, TBillingOrderBy } from "../interfaces/billingSubscriptions.interface";
import { SubscriptionPaymentHistoryModal } from "./SubscriptionPaymentHistoryModal";

interface BillingSubscriptionsTableProps {
  items: IBillingSubscriptionItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  isLoading: boolean;
  search: string;
  statusFilter: string;
  order: TBillingOrder;
  orderBy: TBillingOrderBy;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onSearchChange: (newSearch: string) => void;
  onStatusFilterChange: (newStatus: string) => void;
  onSortChange: (newOrderBy: TBillingOrderBy, newOrder: TBillingOrder) => void;
}

export const BillingSubscriptionsTable: React.FC<BillingSubscriptionsTableProps> = ({
  items,
  page,
  pageSize,
  totalItems,
  totalPages,
  isLoading,
  search,
  statusFilter,
  order,
  orderBy,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onStatusFilterChange,
  onSortChange,
}) => {
  const handleRequestSort = (property: TBillingOrderBy) => {
    const isAsc = orderBy === property && order === "ASC";
    onSortChange(property, isAsc ? "DESC" : "ASC");
  };
  const getStatusBadge = (status: string, paypalId?: string) => {
    const normalized = (status || "").toLowerCase().replace(/[^a-z]/g, "");

    let label = status || "Unknown";
    let color = "#475569";
    let bgcolor = "#f1f5f9";

    if (paypalId === "free_trial" || normalized.includes("trial")) {
      label = "Free Trial";
      color = "#7e22ce";
      bgcolor = "#f3e8ff";
    } else if (normalized === "active" || normalized === "paid") {
      label = normalized === "paid" ? "Paid" : "Active";
      const style = subscriptionStatusColors.active;
      color = style?.color || "#15803D";
      bgcolor = style?.bg || "rgba(21, 128, 61, 0.12)";
    } else if (normalized === "completed") {
      label = "Completed";
      const style = subscriptionStatusColors.completed;
      color = style?.color || "#4474CF";
      bgcolor = style?.bg || "rgba(68, 116, 207, 0.12)";
    } else if (normalized === "failed" || normalized === "pastdue" || normalized === "past_due") {
      label = "Past Due";
      const style = subscriptionStatusColors.failed;
      color = style?.color || "#DC2626";
      bgcolor = style?.bg || "rgba(220, 38, 38, 0.12)";
    } else if (normalized === "cancelled" || normalized === "canceled") {
      label = "Cancelled";
      const style = subscriptionStatusColors.cancelled;
      color = style?.color || "#991B1B";
      bgcolor = style?.bg || "rgba(153, 27, 27, 0.12)";
    } else if (normalized === "suspended") {
      label = "Suspended";
      const style = subscriptionStatusColors.suspended;
      color = style?.color || "#D97706";
      bgcolor = style?.bg || "rgba(217, 119, 6, 0.12)";
    } else if (normalized === "approvalpending" || normalized === "pending") {
      label = "Pending";
      const style = subscriptionStatusColors.approvalPending;
      color = style?.color || "#B86E00";
      bgcolor = style?.bg || "rgba(184, 110, 0, 0.12)";
    } else {
      const style = subscriptionStatusColors[normalized as keyof typeof subscriptionStatusColors];
      if (style) {
        color = style.color;
        bgcolor = style.bg;
      }
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor,
          color,
          fontWeight: 700,
          fontSize: "12px",
          borderRadius: "16px",
          px: 1,
        }}
      />
    );
  };

  const getPriceDisplay = (item: IBillingSubscriptionItem) => {
    // Read basePrice from item.plan.meta.basePrice, default to 0 if missing
    const basePrice = item.plan?.meta?.basePrice ?? 0;
    const currency = item.plan?.meta?.currency ?? "USD";
    const symbol = currency === "USD" ? "$" : `${currency} `;
    return `${symbol}${basePrice}`;
  };

  const getSubscriptionIdentifier = (item: IBillingSubscriptionItem) => {
    return item.paypalId.toUpperCase();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "20px",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: "1px solid var(--fx-surface-border, #E2E8F0)",

        overflow: "hidden",
      }}
    >
      {/* Table Filters & Toolbar */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
          Subscriptions
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {/* Search Box */}
          <TextField
            size="small"
            placeholder="Search by company..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 220 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                fontSize: "13px",
              },
            }}
          />

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                </InputAdornment>
              }
              sx={{
                borderRadius: "12px",
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                fontSize: "13px",
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Past Due</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Table Container */}
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "var(--fx-surface-sunken, #F8FAFC)" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "planName"}
                  direction={orderBy === "planName" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("planName")}
                >
                  PLAN
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "subscriptionId"}
                  direction={orderBy === "subscriptionId" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("subscriptionId")}
                >
                  Subscription ID
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "clientName"}
                  direction={orderBy === "clientName" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("clientName")}
                >
                  CUSTOMER
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  STATUS
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={orderBy === "amount" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("amount")}
                >
                  AMOUNT
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  CREATED DATE
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <TableSortLabel
                  active={orderBy === "expDate"}
                  direction={orderBy === "expDate" ? (order.toLowerCase() as "asc" | "desc") : "asc"}
                  onClick={() => handleRequestSort("expDate")}
                >
                  EXPIRATION DATE
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 700, fontSize: "11px", color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              Array.from({ length: pageSize || 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton variant="text" width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={110} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={70} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={90} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontWeight: 600 }}>
                    No subscriptions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const customerName = item?.company?.businessName;
                const subscriptionId = getSubscriptionIdentifier(item);
                const modalKey = `Subscription-PaymentHistory-${item.id}`;

                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      transition: "background-color 0.15s ease",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    {/* Plan */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {item.plan?.hexColor && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: item.plan.hexColor,
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                          {item.plan?.product?.name || "Standard Plan"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                          {subscriptionId}
                        </Typography>
                        <Tooltip title="Copy ID">
                          <IconButton size="small" onClick={() => copyToClipboard(item.id)}>
                            <ContentCopyIcon sx={{ fontSize: 13, color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* Customer */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                        {customerName}
                      </Typography>
                    </TableCell>

                    {/* Status Badge */}
                    <TableCell>{getStatusBadge(item.status, item.paypalId)}</TableCell>

                    {/* Amount */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
                        {getPriceDisplay(item)}
                      </Typography>
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500 }}>
                        {item.createdAt ? dayjs(item.createdAt).format("MMM DD, YYYY") : "N/A"}
                      </Typography>
                    </TableCell>

                    {/* Expiration Date */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500 }}>
                        {item.expDate ? dayjs(item.expDate).format("MMM DD, YYYY") : "N/A"}
                      </Typography>
                    </TableCell>

                    {/* Actions: View Payment History Modal */}
                    <TableCell align="center">
                      <BaseModalComponent
                        modalKey={modalKey}
                        modalLabel={`Payment History · ${customerName || "Subscription"}`}
                        defaultSizes={{ width: 820, height: 600 }}
                        elementTrigger={
                          <Tooltip title="View Payment History">
                            <IconButton
                              size="small"
                              sx={{ color: "#2563eb", bgcolor: "rgba(37, 99, 235, 0.08)", "&:hover": { bgcolor: "rgba(37, 99, 235, 0.16)" } }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <SubscriptionPaymentHistoryModal subscription={item} />
                      </BaseModalComponent>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination Footer */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>
            Rows per page:
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              height: 32,
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          <Typography variant="caption" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500, ml: 1 }}>
            Total: {totalItems} items
          </Typography>
        </Box>

        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={(_, newPage) => onPageChange(newPage)}
          color="primary"
          shape="rounded"
          size="small"
        />
      </Box>
    </Paper>
  );
};
